import React, { useState } from "react";

type ProcessStep = {
  id: string;
  title: string;
  description: string;
  image: string;
};

const steps: ProcessStep[] = [
  {
    id: "01",
    title: "Identify Your Occupation",
    description:
      "Check whether your occupation is listed on the Skilled Occupation List.",
    image: "/assets/process/step-01-occupation.jpg",
  },
  {
    id: "02",
    title: "Skills Assessment",
    description:
      "Apply to the relevant assessing authority and obtain a positive skills assessment.",
    image: "/assets/process/step-02-skills-assessment.jpg",
  },
  {
    id: "03",
    title: "English Language Test",
    description:
      "Take an approved test such as IELTS, PTE, or TOEFL and achieve the required score.",
    image: "/assets/process/step-03-english-test.jpg",
  },
  {
    id: "04",
    title: "Calculate Your Points",
    description:
      "Confirm that you score at least 65 points based on key eligibility criteria.",
    image: "/assets/process/step-04-calculate-points.jpg",
  },
  {
    id: "05",
    title: "Submit Expression of Interest (EOI)",
    description:
      "Lodge your EOI through SkillSelect and wait for an invitation to apply.",
    image: "/assets/process/step-05-eoi.jpg",
  },
  {
    id: "06",
    title: "Lodge Visa Application",
    description: "Submit all required documents and pay the applicable visa fee.",
    image: "/assets/process/step-06-visa-application.jpg",
  },
  {
    id: "07",
    title: "Receive Visa Grant",
    description:
      "Once approved, receive your visa and move to Australia within the specified timeframe.",
    image: "/assets/process/step-07-visa-grant.jpg",
  },
];



export default function ProcessSection() {
  const [hoveredStep, setHoveredStep] = useState(0);
  return (
    <section
      className="w-full bg-white py-20 md:py-[80px]"
      style={{ minHeight: 380 }}
    >
      <div
        className="max-w-[1280px] mx-auto px-4 md:px-8"
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}
      >
        {/* LEFT COLUMN: Heading + Steps */}
        <div className="flex flex-col items-start w-full">
          <h2
            className="font-bold text-[#111]"
            style={{
              fontSize: '68px',
              lineHeight: 1.08,
              fontWeight: 800,
              marginBottom: 32,
              marginTop: -24,
              letterSpacing: '-1.5px',
            }}
          >
            Our Process
          </h2>
          <div className="w-full flex flex-col">
            {steps.map((step, idx) => (
              <div
                key={step.id}
                className={`group transition-all duration-200 cursor-pointer mb-5 ${idx === 0 ? 'process-step-active' : ''}`}
                style={{ marginBottom: idx === steps.length - 1 ? 0 : 20 }}
                tabIndex={0}
                onMouseEnter={() => setHoveredStep(idx)}
                onFocus={() => setHoveredStep(idx)}
                onMouseLeave={() => setHoveredStep(hoveredStep)}
              >
                <div
                  className={`step-title dock-effect underline-hover`}
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: idx === 0 ? '#0056b8' : '#222',
                    transition: 'color 0.2s, transform 0.18s cubic-bezier(.4,0,.2,1)',
                    cursor: 'pointer',
                    display: 'inline-block',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                  }}
                  onMouseMove={e => {
                    (e.currentTarget as HTMLDivElement).style.transform = 'scale(1.12)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.transform = 'scale(1)';
                  }}
                >
                  {step.title}
                </div>
                {/* Step description removed as per request */}
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT COLUMN: Image + Overlay */}
        <div className="relative w-full flex items-center justify-center">
          <div
            className="process-image-container"
            style={{
              width: '100%',
              maxWidth: 480,
              aspectRatio: '4/5',
              borderRadius: 20,
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
              position: 'relative',
              background: '#eee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div style={{position:'relative',width:'100%',height:'100%'}}>
              {/* Animated image */}
              <img
                key={steps[hoveredStep].image}
                src={steps[hoveredStep].image}
                alt={steps[hoveredStep].title}
                className="process-animated-image"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 20,
                  display: 'block',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  opacity: 1,
                  transition: 'opacity 0.18s cubic-bezier(.4,0,.2,1), transform 0.18s cubic-bezier(.4,0,.2,1)',
                  zIndex: 1,
                }}
              />
              {/* Large step number in image, top-left */}
              <div style={{
                position:'absolute',
                top:18,
                left:18,
                fontSize:'64px',
                fontWeight:700,
                color:'white',
                opacity:0.92,
                textShadow:'0 2px 16px rgba(0,0,0,0.25)',
                zIndex:3,
                letterSpacing:'-2px',
                lineHeight:1,
                fontFamily:'inherit',
                userSelect:'none',
                pointerEvents:'none',
                minWidth:'64px',
                textAlign:'left',
              }}>{`0${hoveredStep+1}`}</div>
              {/* Animated overlay card */}
              <div
                key={steps[hoveredStep].title + '-card'}
                className="process-overlay-card process-animated-card"
                style={{
                  position: 'absolute',
                  bottom: 24,
                  left: 24,
                  width: '75%',
                  maxWidth: 320,
                  background: 'linear-gradient(135deg, rgba(0,123,255,0.9), rgba(0,123,255,0.7))',
                  borderRadius: 16,
                  padding: 20,
                  boxShadow: '0 4px 24px 0 rgba(0,123,255,0.18)',
                  color: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  zIndex: 2,
                  opacity: 1,
                  transition: 'opacity 0.18s cubic-bezier(.4,0,.2,1), transform 0.18s cubic-bezier(.4,0,.2,1)',
                }}
              >
                <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8, color: '#fff' }}>{steps[hoveredStep].title}</div>
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', lineHeight: 1.4, marginBottom: 0 }}>{steps[hoveredStep].description}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Animation, Dock, & Responsive styles */}
      <style>{`
        .process-animated-image, .process-animated-card {
          opacity: 0;
          transform: scale(0.98);
          animation: fadeInFast 0.18s cubic-bezier(.4,0,.2,1) forwards;
        }
        .process-animated-image, .process-animated-card {
          opacity: 1;
          transform: scale(1);
        }
        .dock-effect {
          will-change: transform;
        }
        .underline-hover {
          position: relative;
          text-decoration: none;
        }
        .underline-hover::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -4px;
          width: 100%;
          height: 3px;
          background: linear-gradient(90deg, #0056b8 0%, #007bff 100%);
          opacity: 0;
          transform: scaleX(0.7);
          transition: opacity 0.18s, transform 0.18s;
          border-radius: 2px;
        }
        .underline-hover:hover::after, .underline-hover:focus::after {
          opacity: 1;
          transform: scaleX(1);
        }
        @keyframes fadeInFast {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
        @media (max-width: 1100px) {
          #process > div { gap: 48px !important; }
          #process h2 { font-size: 44px !important; }
        }
        @media (max-width: 900px) {
          #process > div { gap: 40px !important; }
          #process h2 { font-size: 38px !important; }
        }
        @media (max-width: 768px) {
          #process > div {
            display: flex !important;
            flex-direction: column !important;
            gap: 0 !important;
          }
          #process h2 { font-size: 32px !important; margin-bottom: 28px !important; }
          .process-image-container { max-width: 100vw !important; margin-top: 36px; }
        }
        @media (max-width: 600px) {
          #process { padding: 40px 0 !important; }
          #process h2 { font-size: 28px !important; margin-bottom: 20px !important; }
          .step-title { font-size: 16px !important; }
          .step-desc { font-size: 13px !important; }
          .process-image-container { border-radius: 14px !important; }
          .process-overlay-card {
            left: 50% !important;
            transform: translateX(-50%) !important;
            width: 90% !important;
            padding: 16px !important;
            bottom: 16px !important;
          }
          .process-overlay-btn { font-size: 12px !important; padding: 7px 12px !important; }
        }
      `}</style>
    </section>
  );
}
